import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Label, Switch, Textarea, ConfirmationDialog } from '../../../components/ui';
import { ChevronDownIcon } from '../../../components/ui/Icons';

// ChevronUpIcon - simple SVG component
const ChevronUpIcon = ({ className = "h-5 w-5", ...props }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

/**
 * PossibleCausesAccordion - Accordion component for Possible Causes section
 * Groups all possible cause fields (human factor, equipment, methods, materials, supplier) in a collapsible accordion
 */
const PossibleCausesAccordion = ({ 
  formData,
  setFormData,
  config,
  fields
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(null);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const applySwitchOff = (switchField, noteField) => {
    setFormData(prev => ({
      ...prev,
      [switchField]: false,
      [noteField]: '',
    }));
  };

  const handleSwitchChange = (group, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        [group.switchField]: true,
      }));
      return;
    }

    const noteText = String(formData[group.noteField] || '').trim();
    if (noteText) {
      setClearConfirm(group);
      return;
    }

    applySwitchOff(group.switchField, group.noteField);
  };

  const closeClearConfirm = () => setClearConfirm(null);

  const confirmClearCause = () => {
    if (!clearConfirm) return;
    applySwitchOff(clearConfirm.switchField, clearConfirm.noteField);
    setClearConfirm(null);
  };

  // Group fields by cause type
  const causeGroups = [
    {
      name: 'humanFactor',
      label: 'Human Factor',
      switchField: 'humanFactor',
      noteField: 'humanFactorNote'
    },
    {
      name: 'equipment',
      label: 'Equipment',
      switchField: 'equipment',
      noteField: 'equipmentNote'
    },
    {
      name: 'methods',
      label: 'Methods',
      switchField: 'methods',
      noteField: 'methodsNote'
    },
    {
      name: 'materials',
      label: 'Materials',
      switchField: 'materials',
      noteField: 'materialsNote'
    },
    {
      name: 'supplier',
      label: 'Supplier',
      switchField: 'supplier',
      noteField: 'supplierNote'
    }
  ];

  return (
    <div className={cn("border rounded-lg", theme.border.light, theme.colors.background)}>
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
          theme.text.primary
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">⚠️</span>
          <span className="font-semibold">Possible Causes</span>
          {(() => {
            const activeCauses = causeGroups.filter(group => formData[group.switchField]).length;
            if (activeCauses > 0) {
              return (
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-xs font-medium",
                  theme.colors.primary,
                  theme.text.inverse
                )}>
                  {activeCauses} active
                </span>
              );
            }
            return null;
          })()}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5" />
        ) : (
          <ChevronDownIcon className="w-5 h-5" />
        )}
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className={cn("p-4 border-t space-y-6", theme.border.light)}>
          {causeGroups.map((group) => {
            const noteField = fields.find(f => f.name === group.noteField);
            const isActive = formData[group.switchField] || false;

            return (
              <div
                key={group.name}
                className={cn(
                  "p-4 rounded-lg border space-y-3",
                  theme.border.subtle,
                  isActive ? theme.colors.backgroundAlt : theme.colors.background
                )}
              >
                {/* Switch Field */}
                <div className="flex items-center justify-between">
                  <Label htmlFor={group.switchField}>
                    {group.label}
                  </Label>
                  <Switch
                    id={group.switchField}
                    checked={isActive}
                    onChange={(checked) => handleSwitchChange(group, checked)}
                    variant="simple"
                  />
                </div>

                {/* Note Field - Only show if switch is active */}
                {isActive && noteField && (
                  <div>
                    <Label htmlFor={group.noteField}>
                      {noteField.label}
                    </Label>
                    <Textarea
                      id={group.noteField}
                      value={formData[group.noteField] || ''}
                      onChange={(e) => handleFieldChange(group.noteField, e.target.value)}
                      placeholder={noteField.placeholder || `Enter ${group.label.toLowerCase()} details`}
                      rows={noteField.rows || 2}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmationDialog
        isOpen={!!clearConfirm}
        onClose={closeClearConfirm}
        onConfirm={confirmClearCause}
        title={`Clear ${clearConfirm?.label || 'cause'} note?`}
        message={
          clearConfirm
            ? `Turning off ${clearConfirm.label} will remove the ${clearConfirm.label} note text. Continue?`
            : 'Turning off this cause will remove its note text. Continue?'
        }
        warningMessage="This cannot be undone unless you cancel the complaint edit without saving."
        confirmText="Clear note"
        cancelText="Keep"
        confirmVariant="destructive"
      />
    </div>
  );
};

export default PossibleCausesAccordion;
